import { Box } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DemanderKbn, Prefectures } from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import TkStepper from "../../components/atoms/TkStepper";
import TkTitle from "../../components/atoms/TkTitle";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { BusinessContext } from "../../context/businessContext";
import { GlobalContext } from "../../context/globalContext";
import { IAPI_RET005_Request } from "../../type/apiType";
import { TkGetCode } from "../../utils/TkGetCode";
import { httpAPI_RET005 } from "./api/request";
import { fakeTableList, schema, retireeStepper, heirStepper } from "./parts";
import { API_RET005_BuildReqParams } from "../../utils/buildRequestParams";
import _ from "lodash";

/**
 * SCRR411113 退職手当金の請求手続き（退職所得の申告書の作成１）
 * 退職手当金請求者が退職所得の受給に関する申告書兼退職所得申告書に必要な退職者自身の情報を登録する。
 * @return SCRR411113
 */
const SCRR411113 = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const context = businessState.job_35;
  const isRetiree = context?.demanderKbn === DemanderKbn["退職者本人"];
  const step = isRetiree ? 8 : 9;
  const [stateTableList, setStateTableList] = useState<any>([]);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));

  // 初期表示
  useEffect(() => {
    let resArr = stateFakeTableList.current;

    for (let i in context) {
      let targetObj = resArr.find((item) => item.keyword === i)!;

      targetObj &&
        (targetObj.setDefaultValue = (context as { [key: string]: any })[i]);
    }

    //氏名
    resArr.find((item) => item.keyword === "staffNameKanji")!.setDefaultValue =
      isRetiree ? context?.demanderName : context?.staffNameKanji;

    // 現住所
    let currentAddress = resArr.find(
      (item) => item.keyword === "currentAddress"
    )!;
    let nowAddress = {
      prefectures: TkGetCode(
        Prefectures,
        isRetiree
          ? context?.demanderPrefectureCd
          : context?.retirementPrefectureCd
      ),
      city: isRetiree ? context?.demanderCity : context?.retirementAddressCity,
      address: isRetiree
        ? context?.demanderAddressOthers
        : context?.retirementAddressOthers,
    };
    currentAddress.setDefaultValue = nowAddress;

    // その年１月１日現在の住所
    let address = resArr.find((item) => item.keyword === "address")!;
    let retirementYearFirstAddress = {
      prefectures: TkGetCode(
        Prefectures,
        context?.retirementYearFirstPrefectureCd!
      ),
      city: context?.retirementYearFirstAddressCity!,
      address: context?.retirementYearAddressOthers!,
    };
    address.setDefaultValue = retirementYearFirstAddress;

    if (context?.demanderPrefectureCd) {
      address.disabled = false;
      address.disabledPrefectures = true;
      address.disabledCity = true;
      address.disabledAddress = false;
    }

    if (state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO) {
      let retireeName = resArr.find((item) => item.keyword === "retireeName")!;
      retireeName.contractorNumber = context?.staffNameKanji;
      retireeName.showLine = true;
    }
    setStateTableList(resArr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 住所検索
  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let zipcode = resArr.find(
      (item) => item.keyword === "retirementYearFirstZipcode"
    )!;
    let address = resArr.find((item) => item.keyword === "address")!;

    zipcode.getPostalCodeValue = (value: any) => {
      const data = value.value;

      let resObj = {
        prefectures: data.address1!,
        city: data && `${data.address2!}`,
        address: `${data.address3!}`,
      };

      resArr.find((item) => item.keyword === "address")!.valueObject = resObj;

      setState({
        ...state,
        errorMsgId: "",
      });

      address.disabled = false;
      address.disabledPrefectures = true;
      address.disabledCity = true;
      address.disabledAddress = false;
    };

    zipcode.getPostalCodeError = (value: any) => {
      setState({
        ...state,
        errorMsgId: value,
      });
    };

    setStateTableList([...resArr]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const makeHttpObj = (obj: any): IAPI_RET005_Request => {
    const reqObj: IAPI_RET005_Request = {
      ...context,
      tempSaveStep: step, // 一時保存ステップ
      contractorNo:
        state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO
          ? businessState.common?.contractorNo!
          : state.authInfo?.contractorNo!, // 共済契約者番号
      businessType: businessState.common?.businessType, // 業務タイプ
      nowZipcode: isRetiree
        ? context?.demanderZipcode
        : context?.retirementZipcode,
      nowPrefectureCd: isRetiree
        ? context?.demanderPrefectureCd
        : context?.retirementPrefectureCd,
      nowAddressCity: isRetiree
        ? context?.demanderCity
        : context?.retirementAddressCity,
      nowAddressOthers: isRetiree
        ? context?.demanderAddressOthers
        : context?.retirementAddressOthers,
      retirementYearFirstZipcode: obj.retirementYearFirstZipcode, // 退職年度初日住所（郵便番号）
      retirementYearFirstPrefectureCd:
        Prefectures[obj.address?.prefectures as keyof typeof Prefectures], // 退職年度初日住所（都道府県）
      retirementYearFirstAddressCity: obj.address?.city, // 退職年度初日住所（市区町村）
      retirementYearAddressOthers: obj.address?.address, // 退職年度初日住所（その他）
      screenId: "SCRR411113",
    };

    return API_RET005_BuildReqParams(reqObj);
  };

  const handleSubmit = async (data: any, index: any) => {
    const dataObj = {
      retirementYearFirstZipcode: data.retirementYearFirstZipcode,
      retirementYearFirstPrefectureCd:
        Prefectures[data.address?.prefectures as keyof typeof Prefectures],
      retirementYearFirstAddressCity: data.address?.city,
      retirementYearAddressOthers: data.address?.address,
    };

    switch (index) {
      case 0: // 一時保存
        // 退職手当金請求更新API実行
        try {
          const res = await httpAPI_RET005({
            ...makeHttpObj(data),
          });

          if (res.resultCd === "I0000") {
            setBusinessState({
              ...businessState,
              job_35: {
                ...context,
                tempSaveStep: step,
                applyId: res.applyId,
                ...dataObj,
              },
            });
            setState({
              ...state,
              errorMsgId: "",
            });
            setOpen(true);
          } else {
            setState({
              ...state,
              errorMsgId: res.resultCd,
            });
          }
        } catch (error) {
          navigate("/error");
        }
        break;

      case 1: // 次へ
        // 持回り情報
        setBusinessState({
          ...businessState,
          common: businessState.common,
          job_35: {
            ...context,
            ...dataObj,
          },
        });
        navigate("/SCRR411115");
        break;
    }
  };

  return (
    <Box data-testid="SCRR411113test">
      <TkStepper
        steps={isRetiree ? retireeStepper : heirStepper}
        activeStep={step - 1}
        savaStep={context?.tempSaveStep}
      />

      <TkTitle>
        【{context?.retireDate?.split("/")[0]}
        年分退職所得の受給に関する申告書兼退職所得申告書】
      </TkTitle>

      <TkFakeTable
        list={stateTableList}
        ruleSchema={schema}
        submitButtonGroup={[
          { submitButtonText: "一時保存" },
          { submitButtonText: "次へ" },
        ]}
        buttonGroup={<TkBackButton />}
        onCheckAndSubmit={(e, index) => handleSubmit(e, index)}
      />

      <TkMessageDialog
        open={open}
        onOky={() => setOpen(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />
    </Box>
  );
};

export default SCRR411113;
